import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, X, Check, Trash2, CalendarHeart, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LOGO = "https://content.webfactorysite.co.uk/20260113092111-Wales-Event-Logo.png?t=1768296071";

const KINDS = [
  { value: "appointment", label: "Appointment", chip: "bg-sky-100 text-[#005EB8] border-sky-200" },
  { value: "meeting", label: "Meeting", chip: "bg-violet-100 text-violet-700 border-violet-200" },
  { value: "todo", label: "To-do", chip: "bg-amber-100 text-amber-700 border-amber-200" },
];
const kindMeta = (k) => KINDS.find((x) => x.value === k) || KINDS[0];

const isoDay = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function monthCells(cursor) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const offset = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  return cells;
}

const inputCls = "h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8] w-full";

export default function PersonalCalendar() {
  const { user } = useAuth();
  const [cursor, setCursor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [items, setItems] = useState([]);
  const [addDate, setAddDate] = useState("");
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: "", kind: "appointment", time: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/my/personal-events");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = (dateStr) => {
    setForm({ title: "", kind: "appointment", time: "", notes: "" });
    setEditId(null);
    setAddDate(dateStr);
  };

  const openEdit = (item) => {
    setForm({ title: item.title, kind: item.kind, time: item.time || "", notes: item.notes || "" });
    setEditId(item.id);
    setAddDate(item.event_date);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await api.patch(`/my/personal-events/${editId}`, { ...form, event_date: addDate });
        toast.success("Updated");
      } else {
        await api.post("/my/personal-events", { ...form, event_date: addDate });
        toast.success("Added to your calendar");
      }
      setAddDate("");
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleDone = async (item) => {
    try {
      await api.patch(`/my/personal-events/${item.id}`, { done: !item.done });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove "${item.title}" from your calendar?`)) return;
    try {
      await api.delete(`/my/personal-events/${item.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const today = isoDay(new Date());
  const itemsFor = (dateStr) => items.filter((i) => i.event_date === dateStr);

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="my-calendar-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src={LOGO} alt="Wales Event Medical Services" className="h-9 w-auto" />
          <div className="min-w-0">
            <p className="font-heading font-bold text-[#0F2547] text-sm leading-tight">My calendar</p>
            <p className="text-[11px] text-slate-500 truncate">Private to {user?.name || "you"} — nobody else can see this</p>
          </div>
          <Link to="/admin" data-testid="my-calendar-back"
            className="ml-auto inline-flex items-center gap-2 text-xs font-semibold px-3 py-2.5 rounded-lg border border-slate-300 text-[#0F2547] hover:border-[#005EB8] hover:text-[#005EB8] transition-colors min-h-[44px]">
            <ChevronLeft className="h-4 w-4" /> Admin
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))} data-testid="mycal-prev"
            className="h-10 w-10 grid place-items-center rounded-lg bg-white border border-slate-200 hover:border-[#005EB8] transition-colors">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <h1 className="font-heading text-lg font-bold text-[#0F2547] flex-1 text-center" data-testid="mycal-month">
            {cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
          </h1>
          <button onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))} data-testid="mycal-next"
            className="h-10 w-10 grid place-items-center rounded-lg bg-white border border-slate-200 hover:border-[#005EB8] transition-colors">
            <ChevronRight className="h-4 w-4" />
          </button>
          <button onClick={() => openAdd(today)} data-testid="mycal-add-btn"
            className="h-10 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] text-white text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-slate-400 mb-1">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <span key={d}>{d}</span>)}
        </div>
        <div className="grid grid-cols-7 gap-1" data-testid="mycal-grid">
          {monthCells(cursor).map((day, i) => {
            if (!day) return <span key={`pad-${i}`} />;
            const dateStr = isoDay(day);
            const dayItems = itemsFor(dateStr);
            return (
              <div key={dateStr} data-testid={`mycal-day-${dateStr}`}
                className={`min-h-[92px] rounded-xl border p-1.5 bg-white ${dateStr === today ? "border-[#005EB8] ring-1 ring-[#005EB8]/30" : "border-slate-200"}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-[11px] font-bold ${dateStr === today ? "text-[#005EB8]" : "text-slate-500"}`}>{day.getDate()}</span>
                  <button onClick={() => openAdd(dateStr)} data-testid={`mycal-day-add-${dateStr}`} title="Add on this day"
                    className="h-5 w-5 grid place-items-center rounded text-slate-300 hover:text-[#005EB8] hover:bg-sky-50 transition-colors">
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
                <div className="space-y-1 mt-0.5">
                  {dayItems.map((item) => {
                    const meta = kindMeta(item.kind);
                    return (
                      <div key={item.id} data-testid={`mycal-item-${item.id}`} onClick={() => openEdit(item)} title="Tap to edit"
                        className={`group rounded-md border px-1.5 py-1 text-left text-[10px] font-semibold cursor-pointer ${meta.chip} ${item.done ? "opacity-50 line-through" : ""}`}>
                        <div className="flex items-start gap-1">
                          {item.kind === "todo" && (
                            <button onClick={(e) => { e.stopPropagation(); toggleDone(item); }} data-testid={`mycal-done-${item.id}`} title={item.done ? "Mark not done" : "Mark done"}
                              className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border border-current grid place-items-center">
                              {item.done && <Check className="h-2.5 w-2.5" />}
                            </button>
                          )}
                          <span className="flex-1 min-w-0 break-words">
                            {item.time && <span className="inline-flex items-center gap-0.5 mr-1"><Clock className="h-2.5 w-2.5" />{item.time}</span>}
                            {item.title}
                          </span>
                          <button onClick={(e) => { e.stopPropagation(); remove(item); }} data-testid={`mycal-delete-${item.id}`} title="Remove"
                            className="mt-0.5 shrink-0 opacity-0 group-hover:opacity-100 hover:text-[#D5281B] transition-opacity">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex gap-4 mt-4 text-[11px] font-bold text-slate-500">
          {KINDS.map((k) => (
            <span key={k.value} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${k.chip}`}>{k.label}</span>
          ))}
        </div>
      </main>

      {addDate && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setAddDate("")} data-testid="mycal-dialog-backdrop">
          <form onSubmit={save} className="bg-white rounded-2xl p-5 w-full max-w-sm space-y-3 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="mycal-form">
            <div className="flex items-center justify-between">
              <p className="font-heading text-lg font-bold text-[#0F2547] flex items-center gap-2">
                <CalendarHeart className="h-5 w-5 text-[#005EB8]" /> {editId ? "Edit — " : ""}{new Date(`${addDate}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
              </p>
              <button type="button" onClick={() => setAddDate("")} data-testid="mycal-cancel" className="h-9 w-9 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors">
                <X className="h-4 w-4 text-slate-500" />
              </button>
            </div>
            <input required autoFocus placeholder="What is it? e.g. Dentist, meeting with Jake" value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              data-testid="mycal-title" className={inputCls} />
            <div className="grid grid-cols-2 gap-2">
              <select value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
                data-testid="mycal-kind" className={inputCls}>
                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
              <input type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                data-testid="mycal-time" title="Time (optional)" className={inputCls} />
            </div>
            <textarea placeholder="Notes (optional)" value={form.notes} rows={2}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              data-testid="mycal-notes"
              className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
            <button type="submit" disabled={saving} data-testid="mycal-save"
              className="w-full h-11 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-bold disabled:opacity-60">
              {saving ? "Saving…" : editId ? "Save changes" : "Add to my calendar"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
